import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID, getEntityByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { DateTime, Interval } from 'luxon';
import type { Document, InsertOneResult } from 'mongodb';
import {
  cleanPresenceRecordFromCourse,
  fixPausedFuture,
  mergePausedFromPast,
} from './attendance/server/attendance-helper.js';
import { Course } from './courses/Course.js';
import { CourseDigital } from './courses/CourseDigital.js';
import { KPF } from './master-data-management/kpf/entities/KPF.js';
import { ChancenwerkUser } from './master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from './master-data-management/users/entities/ExternerMitarbeiter.js';
import { BuT, Student } from './master-data-management/users/entities/Student.js';
import { TemporaryUser } from './master-data-management/users/entities/TemporaryUser.js';

async function getCourseIncludingArchived(courseID?: string) {
  if (!courseID) {
    return null;
  }

  //! we need to use rawCollection here, because publishFilter filters out archived courses by default
  const courses = await getRawCollection<Course>(Course._collectionName);
  const course = await courses.findOne({ _id: courseID });
  if (!course) {
    return null;
  }

  const constructedCourse = constructValue(course) as Course;
  constructedCourse.leKos = (await loadMongoData(
    ChancenwerkUser,
    constructedCourse.leKoIDs ?? [],
  )) as ChancenwerkUser[];
  constructedCourse.osStudents = (await loadMongoData(Student, constructedCourse.osStudentIDs ?? [])) as Student[];
  constructedCourse.usStudents = (await loadMongoData(Student, constructedCourse.usStudentIDs ?? [])) as Student[];
  return constructedCourse;
}

export enum Status {
  NOT_SURE = 'NOT_SURE',
  PRESENT = 'PRESENT',
  MISSING = 'MISSING',
  EXCUSED = 'EXCUSED',
  PAUSED = 'PAUSED',
  SCHEDULED_ABSENCE = 'SCHEDULED_ABSENCE', // this is a kind of pseudo status because it is only used for the export; if students are not scheduled they are sensibly not shown during attendance check
  NOT_YET_REGISTERED = 'NOT_YET_REGISTERED',
}

export const STATUS_TO_CSV = {
  [Status.PRESENT]: 'a',
  [Status.EXCUSED]: 'e',
  [Status.MISSING]: 'f',
  [Status.PAUSED]: 'p',
  [Status.SCHEDULED_ABSENCE]: 'n', // "planmäßig _N_icht anwesend"
  [Status.NOT_YET_REGISTERED]: 'r', // "noch nicht angemeldet/_R_egistriert"
};

@Entity()
export class UserPresenceRecordFilter extends AdornisFilter {
  static override _class = 'UserPresenceRecordFilter';

  constructor(doc: PartialEntityData<UserPresenceRecordFilter>) {
    super(doc);
  }

  @Field(() => Boolean)
  hasBuT: Maybe<boolean>;

  @Field(() => DateTime)
  hasBuTFrom: Maybe<DateTime>;

  @Field(() => DateTime)
  hasBuTTill: Maybe<DateTime>;

  @Field(() => String)
  name: Maybe<string>;
}

@Entity()
export class UserPresenceRecord extends AdornisEntity {
  public static override _class = 'UserPresenceRecord';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id?: string;

  @Field(() => ID)
  public userID?: string;

  @Field(() => ChancenwerkUser, {
    resolve: function (this: UserPresenceRecord) {
      return async () => {
        // ! we need to use raw collection here for now, because this could be a leko
        // ! from another kpf which we normally dont have access to.
        // TODO: solve this in a better way
        const users = await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName);
        return (await users.findOne({ _id: this.userID ?? '' })) as Maybe<ChancenwerkUser>;

        // OLD: ChancenwerkUser.getDataloader().getByID(this.userID ?? '') as Promise<Maybe<ChancenwerkUser>>
      };
    },
  })
  public user?: ChancenwerkUser;

  @Field(() => String, {
    default: v => v ?? Status.NOT_SURE,
  })
  public status?: Status;

  @Field(() => String, {
    default: v => v ?? '',
  })
  public comment?: string;

  @Field(() => String, {
    default: v => v ?? '',
  })
  public excuseReason?: string;

  @Field(() => DateTime, {
    default: v => v ?? DateTime.now(),
  })
  public date?: DateTime;

  public get statusTranslated(): string {
    switch (this.status) {
      case Status.NOT_SURE:
        return 'noch nicht überprüft';
      case Status.PRESENT:
        return 'anwesend';
      case Status.MISSING:
        return 'abwesend';
      case Status.EXCUSED:
        return 'entschuldigt';
      case Status.PAUSED:
        return 'pausiert';
      case Status.SCHEDULED_ABSENCE:
        return 'planmäßig nicht anwesend';
      case Status.NOT_YET_REGISTERED:
        return 'noch nicht angemeldet';
      default:
        return `Status ${this.status} is not defined!`;
    }
  }
}

@Entity()
export class PresenceRecord extends MongoEntity {
  public static override _class = 'PresenceRecord';
  public static override _collectionName = 'presence-record';

  constructor(doc: PartialEntityData<PresenceRecord>) {
    super(doc);
  }

  @Field(() => ID)
  public courseID?: string;

  @Field(() => Course, {
    resolve: function (this: PresenceRecord) {
      return async () => getCourseIncludingArchived(this.courseID);
    },
  })
  public course?: Course;

  @Field(() => SchuKoPresenceRecord, {
    resolve: function (this: PresenceRecord) {
      return async () => {
        if (!this.courseID) return null;
        return PresenceRecord.getSchuKoPresenceRecordForCourseAndDate(
          this.courseID,
          this.date,
        )({
          _class: 1,
          _id: 1,
          kpfID: 1,
          date: 1,
          schukoRecords: {
            schuKoIDs: 1,
            _id: 1,
            _class: 1,
            user: {
              _class: 1,
              _id: 1,
              vorname: 1,
              nachname: 1,
              email: 1,
            },
            userID: 1,
            status: 1,
            comment: 1,
            excuseReason: 1,
            date: 1,
          },
        });
      };
    },
  })
  public schukoRecords?: SchuKoPresenceRecord;

  public get schukoRecordsFiltered(): UserPresenceRecord[] | null {
    if (!this.schukoRecords) {
      return null;
    }

    return (this.schukoRecords.schukoRecords ?? []).filter(r => {
      return r.status !== Status.NOT_SURE || this.course?.schuKoIDs?.find(u => u === r.userID);
    });
  }

  @Field(() => DateTime, {
    default: v => v ?? DateTime.now().startOf('day'),
  })
  public date!: DateTime;

  get sortableDate() {
    return this.date.set({ hour: this.course?.startTime?.hour, minute: this.course?.startTime?.minute });
  }

  get key() {
    return `${this.date.toFormat('dd.MM.yyyy')}_${this.course?.startTime?.toFormat('HH:mm') ?? '??:??'}`;
  }

  @Field(() => [UserPresenceRecord])
  public studentRecordsUS?: UserPresenceRecord[];

  @Field(() => [UserPresenceRecord])
  public studentRecordsOS?: UserPresenceRecord[];

  @Field(() => [UserPresenceRecord])
  public lekoRecords?: UserPresenceRecord[];

  public get studentRecords() {
    return [...(this.studentRecordsOS ?? []), ...(this.studentRecordsUS ?? [])];
  }

  public get lekoSchukoRecords() {
    return [...(this.lekoRecords ?? []), ...(this.schukoRecordsFiltered ?? [])];
  }

  /**
   * Maps the records by key.
   * @param records The records.
   * @returns The mapped records.
   */
  public static presenceRecordMappedByKey(records: PresenceRecord[]) {
    return records.reduce(
      (acc, record) => {
        acc[record.key] ??= [];
        acc[record.key]?.push(record);
        return acc;
      },
      {} as Record<string, PresenceRecord[]>,
    );
  }

  /**
   * Finds a user record in the presence record by dd.MM.yyyy_HH:mm key
   * @param records Records to search in
   * @param key Key to search for
   * @param type Type of user record to search for
   * @param userID User ID to search for
   * @returns UserPresenceRecord or undefined
   */
  public static findUserRecord(
    records: Record<string, PresenceRecord[]>,
    key: string,
    type: 'LekoSchuko' | 'Student',
    userID: string,
  ) {
    const byKey = records[key] ?? [];

    switch (type) {
      case 'LekoSchuko':
        return byKey
          .find(r => r.lekoSchukoRecords.find(ou => ou.userID === userID && ou.status !== Status.NOT_SURE))
          ?.lekoSchukoRecords.find(ou => ou.userID === userID && ou.status !== Status.NOT_SURE);
      case 'Student':
        return byKey
          .find(r => r.studentRecords.find(ou => ou.userID === userID && ou.status !== Status.NOT_SURE))
          ?.studentRecords.find(ou => ou.userID === userID && ou.status !== Status.NOT_SURE);
    }
  }

  public static isUserAssignedToRecord(
    records: Record<string, PresenceRecord[]>,
    key: string,
    type: 'LekoSchuko' | 'Student',
    userID: string,
  ) {
    const byKey = records[key] ?? [];

    switch (type) {
      case 'LekoSchuko':
        return byKey.some(record => record.lekoSchukoRecords.some(ou => ou.userID === userID));
      case 'Student':
        return byKey.some(record => record.studentRecords.some(ou => ou.userID === userID));
    }
  }

  private static buildDayCommentsCSV(records: PresenceRecord[]) {
    const header = '#;datum;uhrzeit;kurs;standort;kommentar';
    if (!records.length) {
      return header;
    }

    const rows = records
      .map((record, index) => {
        const date = record.date?.toFormat('dd.MM.yyyy') ?? '';
        const startTime = record.course?.startTime?.toFormat('HH:mm') ?? '';
        const courseName = record.course?.name ?? '';
        const location = record.course?.location ?? '';
        const comment = (record.comment ?? '').replace(/\r?\n/g, ' ').trim();

        return `${index + 1};${date};${startTime};${courseName};${location};${comment}`;
      })
      .join('\n');

    return `${header}\n${rows}`;
  }

  @Field(() => String, {
    default: v => v ?? '',
  })
  public comment?: string;

  static override async transformBeforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: MongoEntity,
  ): Promise<void> {
    await super.transformBeforeUpdate(oldEntity, update);
    const record = (await getByID(
      PresenceRecord,
      update._id,
    )({
      ...PresenceRecord.allFields,
      course: {
        _id: 1,
        _class: 1,
        kpfID: 1,
        osStudentIDs: 1,
        usStudentIDs: 1,
        leKoIDs: 1,
      },
    })) as unknown as PresenceRecord | null;
    await fixPausedFuture(record);
  }

  static override async afterCreate(entity: PresenceRecord, result: InsertOneResult<Document>) {
    await super.afterCreate(entity, result);
    const record = (await getByID(
      PresenceRecord,
      entity._id,
    )({
      ...PresenceRecord.allFields,
      course: {
        _id: 1,
        _class: 1,
        kpfID: 1,
        osStudentIDs: 1,
        usStudentIDs: 1,
        leKoIDs: 1,
      },
    })) as unknown as PresenceRecord | null;
    await fixPausedFuture(record);
  }

  @Query(() => PresenceRecord)
  static getPresenceRecordForCourseAndDate(
    @Arg('courseID', () => String) courseID: string,
    @Arg('date', () => DateTime) date: DateTime,
  ) {
    return async (gqlFields: BaseQLSelectionSet<PresenceRecord>) => {
      if (!courseID) return null;

      const course = await getCourseIncludingArchived(courseID);

      if (!course?._id) throw new Error('Couldnt find course with id: ' + courseID.toString());

      const presenceRecordCollection = await getCollection(PresenceRecord._class);
      const presenceRecord = (await presenceRecordCollection.findOne({
        courseID: course._id,
        date: date.startOf('day'),
      })) as PresenceRecord | null;

      const courses = await getRawCollection<Course>(Course._collectionName);
      const coursesId = await courses
        .find(
          {
            kpfID: course.kpfID,
          },
          {
            projection: {
              _id: 1,
            },
          },
        )
        .toArray();

      // Find 15 last PresenseRecords for the courses
      const lastStates = (await presenceRecordCollection
        .find(
          { courseID: { $in: coursesId.map(c => c._id) }, date: { $lte: date.toJSDate() } },
          { sort: [['date', -1]], limit: 15 },
        )
        .toArray()) as PresenceRecord[] | null;

      // If no presence record is found generate a new one
      if (!presenceRecord) {
        const generated = new PresenceRecord({
          courseID: course._id,
          lekoRecords: course.leKos?.map(user => new UserPresenceRecord({ userID: user._id, user })),
          studentRecordsOS: course.osStudents.map(user => new UserPresenceRecord({ userID: user._id, user })),
          studentRecordsUS: course.usStudents.map(user => new UserPresenceRecord({ userID: user._id, user })),
        });

        generated.course = course;
        generated.date = date.startOf('day');

        if (course._class !== CourseDigital._class) {
          generated.schukoRecords = await SchuKoPresenceRecord.getSchuKoPresenceRecordForDateAndKPF(
            date.startOf('day'),
            course.kpfID,
          )(SchuKoPresenceRecord.allFields);
        }

        mergePausedFromPast(lastStates, generated);

        return generated;
      }

      cleanPresenceRecordFromCourse(course, presenceRecord);
      mergePausedFromPast(lastStates, presenceRecord);

      return constructValue(presenceRecord) as PresenceRecord;
    };
  }

  @Query(() => [PresenceRecord])
  static getPresenceRecordForCourse(
    @Arg('courseID', () => String) courseID: string,
    @Arg('timestamp', () => Float) timestamp: number,
  ) {
    return async (gqlFields: BaseQLSelectionSet<PresenceRecord>) => {
      if (!courseID) return null;

      const course = await getCourseIncludingArchived(courseID);

      if (!course?._id) {
        return [];
      }

      // * Restrict to the same ±8 week window the attendance calendar actually renders
      // * (see EVENTS_WEEKS in chancenboard's generate-events.ts). Without this filter
      // * the query loaded *every* PresenceRecord ever created for the course, which on
      // * long-lived KPFs is the primary OOM trigger when the page subscribes to dozens
      // * of (incl. archived) courses in parallel.
      const anchor = DateTime.fromMillis(timestamp);
      const from = anchor.minus({ weeks: 8 }).startOf('week').toJSDate();
      const to = anchor.plus({ weeks: 8 }).endOf('week').toJSDate();

      const presenceRecordCollection = await getRawCollection<PresenceRecord>(PresenceRecord._collectionName);
      const presenceRecords = await presenceRecordCollection
        .find({
          _class: PresenceRecord._class,
          courseID: course._id,
          // * `date` is typed as Luxon DateTime on the entity but stored as BSON Date,
          // * so we pass JS Dates at runtime. Cast mirrors the existing pattern used by
          // * getPresenceRecordForCourseAndDateRange below.
          date: { $gte: from, $lte: to } as unknown as DateTime,
        })
        .toArray();

      return presenceRecords.map(r => constructValue(r) as PresenceRecord);
    };
  }

  @Query(() => SchuKoPresenceRecord)
  static getSchuKoPresenceRecordForCourseAndDate(
    @Arg('courseID', () => String) courseID: string,
    @Arg('date', () => DateTime) date: DateTime,
  ) {
    return async (gqlFields: BaseQLSelectionSet<SchuKoPresenceRecord>) => {
      if (!courseID) return null;

      const collection = await getCollection<Course>(Course._class);
      const course = (await collection.findOne(
        { _id: courseID },
        { projection: { _id: 1, kpfID: 1 } },
      )) as Course | null;
      if (!course) return null;

      return SchuKoPresenceRecord.getSchuKoPresenceRecordForDateAndKPF(date, course.kpfID)(gqlFields);
    };
  }

  @Query(() => [PresenceRecord])
  static getPresenceRecordForCourseAndDateRange(
    @Arg('courseID', () => String) courseID: string,
    @Arg('from', () => DateTime) from: DateTime,
    @Arg('to', () => DateTime) to: DateTime,
  ) {
    return async (gqlFields: BaseQLSelectionSet<PresenceRecord>) => {
      if (!courseID) return null;

      const courses = await getRawCollection<Course>(Course._collectionName); //! we need to use rawCollection here, because publishFilter filters out archived courses by default
      const course = await courses.findOne({ _id: courseID }, { projection: { _id: 1 } });

      if (!course?._id) {
        return [];
      }

      const presenceRecordCollection = await getCollection(PresenceRecord._class);
      const presenceRecordsRaw = await presenceRecordCollection
        .find(
          {
            _class: PresenceRecord._class,
            courseID: course._id,
            date: {
              $gte: from.toJSDate(),
              $lte: to.toJSDate(),
            },
          },
          {
            sort: 'date',
            projection: {
              _id: 1,
            },
          },
        )
        .toArray();
      const presenceRecords = await getEntityByID(
        PresenceRecord,
        presenceRecordsRaw.map(r => r._id),
      )({
        ...gqlFields,
        sortableDate: 1,
        date: 1,
        course: {
          startTime: 1,
          usStudentIDs: 1,
          osStudentIDs: 1,
          leKoIDs: 1,
          schuKoIDs: 1,
        },
      });

      return presenceRecords.sort((a, b) => a.sortableDate.toMillis() - b.sortableDate.toMillis());
    };
  }

  @Query(() => [Student])
  public static getStudentsFromRecords(
    @Arg('recordIDs', () => [String]) recordIDs: string[],
    @Arg('filter', () => UserPresenceRecordFilter) filter?: Maybe<UserPresenceRecordFilter>,
  ) {
    return async (gqlFields: BaseQLSelectionSet<Student>) => {
      const records = await getEntityByID(
        PresenceRecord,
        recordIDs,
      )({
        ...PresenceRecord.allFields,
        schukoRecords: {
          ...SchuKoPresenceRecord.allFields,
          schukoRecords: UserPresenceRecord.allFields,
        },
      });

      const users: Record<string, Student> = {};
      for (const r of records) {
        for (const s of r.studentRecords) {
          if (!s.userID || users[s.userID]) {
            continue;
          }

          const student = await getByID(
            Student,
            s.userID,
          )({
            _id: 1,
            _class: 1,
            ...gqlFields,
            vorname: 1,
            nachname: 1,
            buts: BuT.allFields,
          });

          if (student) {
            // * If BuT filter is applied we check if the from and to date
            // * are inside the bounds of the BuT
            if (
              filter?.hasBuT &&
              !student.buts.some(b => {
                if (!b.BuTBerechtigung) {
                  return false;
                }

                const bfrom = b.BuTBewilligungAb ?? b.BuTBewilligungBis;
                const bto = b.BuTBewilligungBis;
                if (!bfrom && !bto) {
                  return false;
                }

                return Interval.fromDateTimes(
                  filter.hasBuTFrom?.startOf('day'),
                  filter.hasBuTTill?.endOf('day'),
                ).intersection(Interval.fromDateTimes(bfrom.startOf('day'), bto.endOf('day')));
              })
            ) {
              continue;
            }

            if (filter?.name) {
              const name = `${student.vorname} ${student.nachname}`.toLowerCase();
              if (!name.includes(filter.name.toLowerCase())) {
                continue;
              }
            }

            users[s.userID] = constructValue(student);
          }
        }
      }
      return Object.values(users);
    };
  }

  @Query(() => [ChancenwerkUser])
  public static getSchuKoLeKosFromRecords(
    @Arg('recordIDs', () => [String]) recordIDs: string[],
    @Arg('filter', () => UserPresenceRecordFilter) filter?: Maybe<UserPresenceRecordFilter>,
  ) {
    return async (gqlFields: BaseQLSelectionSet<ChancenwerkUser>) => {
      const records = await PresenceRecord.getEntityByID<PresenceRecord>(
        PresenceRecord._class,
        recordIDs,
      )({
        date: 1,
        schukoRecords: {
          schukoRecords: {
            userID: 1,
          },
        },
        lekoRecords: {
          userID: 1,
        },
      });

      const users: Set<string> = new Set();
      for (const r of records) {
        for (const s of [...(r.schukoRecords?.schukoRecords ?? []), ...(r.lekoRecords ?? [])]) {
          if (!s.userID || users.has(s.userID)) {
            continue;
          }

          users.add(s.userID);
        }
      }

      const allUsers = await loadMongoData(ChancenwerkUser, [...users.keys()]);
      return allUsers.filter(u => {
        if (filter?.name) {
          const name = `${u?.vorname} ${u?.nachname}`.toLowerCase();
          if (!name.includes(filter.name.toLowerCase())) {
            return false;
          }
        }
        return true;
      });
    };
  }

  @Query(() => [String])
  public static exportMultipleStudentPresenceRecordsCSV(
    @Arg('courseID', () => [String]) courseIDs: Maybe<string[]>,
    @Arg('from', () => DateTime) from: DateTime,
    @Arg('to', () => DateTime) to: DateTime,
    @Arg('hasBuT', () => Boolean) hasBuT: boolean = false,
  ) {
    // TODO: make this less cancer
    return async (gqlFields: void) => {
      const header = `#;name;vorname;männlich/weiblich/divers;stufe;typ`;

      if (!courseIDs || courseIDs.length === 0) {
        return [header, header, PresenceRecord.buildDayCommentsCSV([])];
      }

      const presenceRecordCollection = await getCollection<PresenceRecord>(PresenceRecord._class);
      const presenceRecordsRaw = await presenceRecordCollection
        .find(
          {
            courseID: { $in: courseIDs },
            date: {
              $gte: from.toJSDate(),
              $lte: to.toJSDate(),
            },
          },
          {
            sort: {
              date: 1,
            },
            limit: 200,
            projection: {
              _id: 1,
            },
          },
        )
        .toArray();
      const presenceRecords = await PresenceRecord.getEntityByID<PresenceRecord>(
        PresenceRecord._class,
        presenceRecordsRaw.map(r => r._id),
      )({
        course: {
          _class: 1,
          location: 1,
          name: 1,
          startTime: 1,
        },
        date: 1,
        sortableDate: 1,
        key: 1,
        ...PresenceRecord.allFields,
        schukoRecords: {
          ...SchuKoPresenceRecord.allFields,
          schukoRecords: UserPresenceRecord.allFields,
        },
      });
      presenceRecords.sort((a, b) => a.sortableDate.toMillis() - b.sortableDate.toMillis());

      // build a list of students that are present over all the records
      const users = await PresenceRecord.getStudentsFromRecords(
        presenceRecords.map(r => r._id),
        new UserPresenceRecordFilter({
          hasBuTFrom: from.toJSDate(),
          hasBuTTill: to.toJSDate(),
          hasBuT,
        }),
      )({
        vorname: 1,
        nachname: 1,
        geschlecht: 1,
        studentID: 1,
        schuelertyp: 1,
        guestAccount: 1,
        key: 1,
        schoolInformation: {
          _class: 1,
          zug: 1,
          jahrgangsstufe: 1,
        },
        joinInformation: {
          eintrittsdatum: 1,
        },
        kuendigung: {
          austrittsdatum: 1,
        },
      });
      const usersMap: Record<string, Student> = {};
      users.forEach(u => {
        usersMap[u._id] = u;
      });

      const timeKeys = new Set<string>(presenceRecords.map(r => r.key));
      const recordHeader = `;${Array.from(timeKeys).join(';')}\n`;
      const recordsMapped = PresenceRecord.presenceRecordMappedByKey(presenceRecords as PresenceRecord[]);

      const usersValues = Object.values(users).filter(u => {
        const austrittsdatum = u.kuendigung?.austrittsdatum;
        return !austrittsdatum || austrittsdatum >= from.startOf('day');
      });

      usersValues.sort((a, b) => {
        const nameA = `${a.nachname} ${a.vorname}`.toLowerCase();
        const nameB = `${b.nachname} ${b.vorname}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      const presence =
        header +
        recordHeader +
        usersValues
          .map((u, i) => {
            return `${u.studentID ?? 'keine id'};${u.nachname};${u.vorname};${u.geschlecht};${
              u.schoolInformation.jahrgangsstufe ?? ''
            }${u.schoolInformation.zug ? ` (${u.schoolInformation.zug})` : ''};${u.guestAccount ? 'Gast-' : ''}${
              u.schuelertyp ?? ''
            };${Array.from(timeKeys)
              .map(timeKey => {
                const userRecord = PresenceRecord.findUserRecord(recordsMapped, timeKey, 'Student', u._id);
                if (!userRecord) {
                  const recordDate = recordsMapped[timeKey]?.[0]?.date;
                  const eintrittsdatum = u.joinInformation?.eintrittsdatum;
                  const austrittsdatum = u.kuendigung?.austrittsdatum;

                  if (recordDate && austrittsdatum && recordDate > austrittsdatum) {
                    return '';
                  }
                  if (recordDate && eintrittsdatum && recordDate < eintrittsdatum) {
                    return STATUS_TO_CSV[Status.NOT_YET_REGISTERED];
                  }

                  const assignedToDay = PresenceRecord.isUserAssignedToRecord(recordsMapped, timeKey, 'Student', u._id);
                  return assignedToDay ? '' : STATUS_TO_CSV[Status.SCHEDULED_ABSENCE];
                }

                return STATUS_TO_CSV[userRecord.status ?? ''] ?? '';
              })
              .join(';')}`;
          })
          .join('\n');

      const commentsAndExcuses =
        header +
        recordHeader +
        usersValues
          .map((u, i) => {
            return `${u.studentID ?? 'keine id'};${u.nachname};${u.vorname};${u.geschlecht ?? ''};${
              u.schoolInformation.jahrgangsstufe ?? ''
            }${u.schoolInformation.zug ? ` (${u.schoolInformation.zug})` : ''};${u.guestAccount ? 'Gast-' : ''}${
              u.schuelertyp ?? ''
            };${Array.from(timeKeys)
              .map(timeKey => {
                const userRecord = PresenceRecord.findUserRecord(recordsMapped, timeKey, 'Student', u._id);
                if (!userRecord) {
                  return '';
                }

                return `${userRecord.comment ? `${userRecord.comment} ` : ''}${
                  userRecord.excuseReason ? `Grund: ${userRecord.excuseReason}` : ''
                }`.trim();
              })
              .join(';')}`;
          })
          .join('\n');

      const dayComments = PresenceRecord.buildDayCommentsCSV(presenceRecords as PresenceRecord[]);

      return [presence, commentsAndExcuses, dayComments];
    };
  }

  @Query(() => [String])
  public static exportMultipleSchuKoLeKoPresenceRecordsCSV(
    @Arg('courseID', () => [String]) courseIDs: Maybe<string[]>,
    @Arg('from', () => DateTime) from: DateTime,
    @Arg('to', () => DateTime) to: DateTime,
  ) {
    // TODO: make this less cancer
    return async (gqlFields: void) => {
      const header = `#;name;vorname;männlich/weiblich/divers;typ`;

      if (!courseIDs || courseIDs.length === 0) {
        return header;
      }

      const courses = await Course.getByID<Course>(courseIDs)({
        leKoIDs: 1,
        schuKoIDs: 1,
      });
      if (!courses) {
        throw 'courses not found';
      }

      const presenceRecordCollection = await getCollection<PresenceRecord>(PresenceRecord._class);
      const presenceRecordsRaw = await presenceRecordCollection
        .find(
          { courseID: { $in: courseIDs }, date: { $gte: from.toJSDate(), $lte: to.toJSDate() } },
          { sort: { date: 1 }, limit: 200, projection: { _id: 1 } },
        )
        .toArray();
      const presenceRecords = await PresenceRecord.getEntityByID<PresenceRecord>(
        PresenceRecord._class,
        presenceRecordsRaw.map(r => r._id),
      )({
        course: {
          _class: 1,
          location: 1,
          name: 1,
          startTime: 1,
        },
        ...PresenceRecord.allFields,
        date: 1,
        sortableDate: 1,
        key: 1,
        schukoRecords: {
          ...SchuKoPresenceRecord.allFields,
          schukoRecords: UserPresenceRecord.allFields,
        },
        lekoRecords: UserPresenceRecord.allFields,
      });
      presenceRecords.sort((a, b) => a.sortableDate.toMillis() - b.sortableDate.toMillis());

      // build a list of students that are present over all the records
      const users = await PresenceRecord.getSchuKoLeKosFromRecords(presenceRecords.map(r => r._id))({
        vorname: 1,
        nachname: 1,
        geschlecht: 1,
        guestAccount: 1,
      });
      const usersMap: Record<string, ChancenwerkUser> = {};
      users.forEach(u => {
        if (u) {
          usersMap[u._id] = u as ChancenwerkUser;
        }
      });

      const timeKeys = new Set<string>(presenceRecords.map(r => r.key));
      const recordHeader = `;${Array.from(timeKeys).join(';')}\n`;

      const presenceRows: string[] = [];
      const commentsAndExcusesRows: string[] = [];

      const recordsMapped = PresenceRecord.presenceRecordMappedByKey(presenceRecords as PresenceRecord[]);

      Object.values(usersMap)
        .sort((a, b) => `${a.nachname} ${a.vorname}`.localeCompare(`${b.nachname} ${b.vorname}`))
        .forEach((u, i) => {
          if (!u) {
            return;
          }

          // Infer the type
          let type = 'LeKo';
          if (presenceRecords.some(r => r.schukoRecords?.schukoRecords?.some(ou => u._id === ou.userID))) {
            type = 'SchuKo';
          }
          if (u._class === TemporaryUser._class || (u instanceof ExternerMitarbeiter && u.guestAccount)) {
            type = `Gast-${type}`;
          }

          let name = `${u.nachname};${u.vorname}`;
          if (!u.vorname && !u.nachname) {
            name = 'SchuKo;SchuKo';
          }

          // * We get the presence status per day and make it ; seperated
          const statusPerDay = Array.from(timeKeys)
            .map(timeKey => {
              const userRecord = PresenceRecord.findUserRecord(recordsMapped, timeKey, 'LekoSchuko', u._id);
              if (!userRecord) {
                return '';
              }

              return STATUS_TO_CSV[userRecord.status ?? ''] ?? '';
            })
            .join(';');

          // * We get the presence comments and excuse reason per day and make it ; seperated
          const commentsAndExcusesPerDay = Array.from(timeKeys)
            .map(timeKey => {
              const userRecord = PresenceRecord.findUserRecord(recordsMapped, timeKey, 'LekoSchuko', u._id);
              if (!userRecord) {
                return '';
              }

              return `${userRecord.comment ? `${userRecord.comment} ` : ''}${
                userRecord.excuseReason ? `Grund: ${userRecord.excuseReason}` : ''
              }`.trim();
            })
            .join(';');

          const fixedValues = `${i + 1};${name};${u.geschlecht ?? ''};${type}`;
          presenceRows.push(`${fixedValues};${statusPerDay}`);
          commentsAndExcusesRows.push(`${fixedValues};${commentsAndExcusesPerDay}`);
        });

      const dayComments = PresenceRecord.buildDayCommentsCSV(presenceRecords as PresenceRecord[]);

      return [
        header + recordHeader + presenceRows.join('\n'),
        header + recordHeader + commentsAndExcusesRows.join('\n'),
        dayComments,
      ];
    };
  }
}

@Entity()
export class SchuKoPresenceRecord extends MongoEntity {
  public static override _class = 'SchuKoPresenceRecord';
  public static override _collectionName = 'presence-record';

  @Field(() => ID)
  public kpfID?: string;

  @Field(() => DateTime, {
    default: v => v ?? DateTime.now().startOf('day'),
  })
  public date!: DateTime;

  @Field(() => [UserPresenceRecord])
  public schukoRecords?: UserPresenceRecord[];

  @Query(() => SchuKoPresenceRecord)
  static getSchuKoPresenceRecordForDateAndKPF(
    @Arg('date', () => DateTime) date: DateTime,
    @Arg('kpfID', () => String) kpfID: string,
  ) {
    return async (gqlFields: BaseQLSelectionSet<SchuKoPresenceRecord>) => {
      if (kpfID === 'GLOBAL') {
        return new SchuKoPresenceRecord({
          class: SchuKoPresenceRecord._class,
          kpfID,
          date: date.startOf('day'),
          schukoRecords: [],
        });
      }

      const kpf = await KPF.getByID<KPF>(kpfID)({
        _id: 1,
        _class: 1,
        schuKos: {
          _id: 1,
        },
      });

      if (!kpf?._id) throw new Error(`Couldnt find kpf with id: ${kpfID.toString()}`);

      const presenceRecordCollection = await getCollection<SchuKoPresenceRecord>(SchuKoPresenceRecord._class);
      const presenceRecord = (await presenceRecordCollection.findOne({
        kpfID,
        date: date.startOf('day'),
      })) as SchuKoPresenceRecord | null;

      if (!presenceRecord) {
        const userCollection = await getRawCollection<EntityData<AdornisUser>>(AdornisUser._collectionName);

        const generated = new SchuKoPresenceRecord({
          class: SchuKoPresenceRecord._class,
          kpfID,
          date: date.startOf('day'),
          schukoRecords: [],
        });

        for (const user of kpf.schuKos ?? []) {
          const cwUser = (await userCollection.findOne({ _id: user._id })) as ChancenwerkUser | null;
          if (cwUser) {
            generated.schukoRecords?.push(new UserPresenceRecord({ userID: user._id, user: cwUser }));
          }
        }

        return generated;
      }

      return constructValue(presenceRecord) as SchuKoPresenceRecord;
    };
  }
}

if (A.isServer) {
  const normal = getRawCollection<any>(PresenceRecord._collectionName);

  // Create DB Indices
  void normal.then(async db => {
    await db.createIndex({ courseID: 1 });
    await db.createIndex({ date: 1 });
    await db.createIndex({ 'studentRecordsUS.userID': 1 });
    await db.createIndex({ 'studentRecordsOS.userID': 1 });
    await db.createIndex({ 'lekoRecords.userID': 1 });
  });

  const schuko = getRawCollection<any>(SchuKoPresenceRecord._collectionName);
  // Create DB Indices
  void schuko.then(async db => {
    await db.createIndex({ kpfID: 1 });
    await db.createIndex({ date: 1 });
    await db.createIndex({ 'schukoRecords.userID': 1 });
  });
}
